import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaDumbbell, FaUserMd, FaPills, FaHeartbeat, FaCheck,
  FaArrowLeft, FaStar, FaShieldAlt, FaClock, FaPercentage
} from 'react-icons/fa';
import { getProgramWithDurations, createOrder, initializeRazorpayCheckout } from '../services/paymentAPI';

const ProgramCheckout = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchProgramDetails();
  }, [programId]);

  const fetchProgramDetails = async () => {
    try {
      const data = await getProgramWithDurations(programId);
      setProgram(data);
      // Default select the most popular option (6 months) or first
      const popularOption = data.durationOptions?.find(opt => opt.badge === 'MOST POPULAR');
      setSelectedDuration(popularOption || data.durationOptions?.[0]);
    } catch (error) {
      console.error('Error fetching program:', error);
      toast.error('Failed to load program details');
      navigate('/pricing');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedDuration) {
      toast.error('Please select a duration');
      return;
    }

    setProcessing(true);
    try {
      const orderData = await createOrder(selectedDuration.planId, selectedDuration.durationMonths);

      await initializeRazorpayCheckout(
        orderData,
        (paymentResult) => {
          toast.success('Payment successful! Welcome to Fitnession!');
          navigate('/dashboard', { state: { paymentSuccess: true } });
        },
        (error) => {
          toast.error(error.description || 'Payment failed. Please try again.');
          setProcessing(false);
        },
        () => {
          setProcessing(false);
        }
      );
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initialize payment. Please try again.');
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getProgramIcon = (programType) => {
    switch (programType) {
      case 'CORE':
        return <FaDumbbell className="text-4xl text-primary-600" />;
      case 'MEDICAL':
        return <FaUserMd className="text-4xl text-primary-600" />;
      case 'GLP1':
        return <FaPills className="text-4xl text-primary-600" />;
      default:
        return <FaHeartbeat className="text-4xl text-primary-600" />;
    }
  };

  const getProgramGradient = (programType) => {
    switch (programType) {
      case 'CORE':
        return 'from-blue-500 to-cyan-500';
      case 'MEDICAL':
        return 'from-purple-500 to-pink-500';
      case 'GLP1':
        return 'from-green-500 to-teal-500';
      default:
        return 'from-primary-500 to-success-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Program not found</p>
          <button
            onClick={() => navigate('/pricing')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan Duration</h1>
          <p className="text-gray-600">Select the duration that works best for you</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Program Summary - Left Column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <div className={`bg-gradient-to-r ${getProgramGradient(program.programType)} text-white rounded-xl p-4 mb-6`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    {getProgramIcon(program.programType)}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{program.programName}</h2>
                    <p className="text-white/80 text-sm">{program.programType} Program</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{program.description}</p>

              <h3 className="font-semibold text-gray-900 mb-3">What's included:</h3>
              <ul className="space-y-2 mb-6">
                {program.features?.slice(0, 6).map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <FaCheck className="text-success-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
                {program.features?.length > 6 && (
                  <li className="text-gray-500 text-sm pl-6">
                    +{program.features.length - 6} more features
                  </li>
                )}
              </ul>

              {/* Trust Badges */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaShieldAlt className="text-success-600" />
                  <span>Secure payment via Razorpay</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaClock className="text-primary-600" />
                  <span>Instant access after payment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Duration Options - Right Column */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {program.durationOptions?.map((option) => (
                <div
                  key={option.durationMonths}
                  onClick={() => setSelectedDuration(option)}
                  className={`relative bg-white rounded-2xl shadow-lg p-6 cursor-pointer transition-all ${
                    selectedDuration?.durationMonths === option.durationMonths
                      ? 'ring-2 ring-primary-500 shadow-xl'
                      : 'hover:shadow-xl'
                  }`}
                >
                  {/* Badge */}
                  {option.badge && (
                    <div className={`absolute -top-3 left-6 px-4 py-1 rounded-full text-sm font-bold text-white ${
                      option.badge === 'BEST VALUE'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : option.badge === 'MOST POPULAR'
                        ? 'bg-gradient-to-r from-primary-500 to-blue-500'
                        : 'bg-gradient-to-r from-purple-500 to-pink-500'
                    }`}>
                      {option.badge === 'MOST POPULAR' && <FaStar className="inline mr-1" />}
                      {option.badge}
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Duration Info */}
                    <div className="flex items-center gap-4">
                      {/* Radio Button */}
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedDuration?.durationMonths === option.durationMonths
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedDuration?.durationMonths === option.durationMonths && (
                          <FaCheck className="text-white text-xs" />
                        )}
                      </div>

                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {option.durationMonths} Month{option.durationMonths > 1 ? 's' : ''}
                        </h3>
                        <p className="text-gray-600">
                          {formatPrice(option.monthlyPrice)}/month
                        </p>
                      </div>
                    </div>

                    {/* Pricing Info */}
                    <div className="flex items-center gap-6">
                      {/* Discount Badge */}
                      {option.discountPercentage > 0 && (
                        <div className="flex items-center gap-1 bg-success-100 text-success-700 px-3 py-1 rounded-full">
                          <FaPercentage className="text-xs" />
                          <span className="font-semibold">{option.discountPercentage}% OFF</span>
                        </div>
                      )}

                      {/* Savings */}
                      {option.savings > 0 && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500 line-through">
                            {formatPrice(option.originalPrice)}
                          </p>
                          <p className="text-xs text-success-600 font-medium">
                            Save {formatPrice(option.savings)}
                          </p>
                        </div>
                      )}

                      {/* Total Price */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          {formatPrice(option.totalPrice)}
                        </p>
                        <p className="text-xs text-gray-500">total</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Summary & CTA */}
            {selectedDuration && (
              <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {program.programName} - {selectedDuration.durationMonths} Month{selectedDuration.durationMonths > 1 ? 's' : ''}
                    </h3>
                    <p className="text-gray-600">
                      {formatPrice(selectedDuration.monthlyPrice)}/month
                      {selectedDuration.savings > 0 && (
                        <span className="text-success-600 ml-2">
                          (You save {formatPrice(selectedDuration.savings)})
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <p className="text-3xl font-bold text-gray-900">
                      {formatPrice(selectedDuration.totalPrice)}
                    </p>
                    <button
                      onClick={handlePayment}
                      disabled={processing}
                      className="bg-gradient-to-r from-primary-600 to-success-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-primary-700 hover:to-success-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {processing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          Proceed to Payment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Secure Payment Info */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <FaShieldAlt className="text-success-600" />
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCheckout;
